/**
 * Life4Earth V2 — Content Sections API Adapter (entity-polymorphic).
 *
 * Was: project-only CRUD at /api/Projects/{guid}/sections.
 * Now: serves both Full Project and Mini-Project via entity_type.
 *
 * Migration 167 (2026-05-19) unified the About backend so Mini-Project
 * and Full Project share `project_content_sections` (extended with
 * entity_type / entity_guid). The API exposes two parallel route
 * families:
 *   /api/Projects/{guid}/sections         — entity_type='project'
 *   /api/mini-projects/{guid}/sections    — entity_type='mini_project'
 *
 * This adapter selects the right route family based on the entityType
 * argument. Callers should pass entityType + entityGuid.
 */

import { apiFetch } from "./client";
import { normalizeGuid } from "../utils/guid";

// ── Types (mirror C# DTOs, camelCase from ASP.NET serializer) ───

export type ContentSectionEntityType = "project" | "mini_project" | "chronicle";

export interface ProjectContentSectionResponse {
  sectionGuid: string;
  /** Legacy field; null for Mini-Project rows post migration 167. */
  projectGuid: string | null;
  /** Polymorphic entity context (migration 167). */
  entityType: ContentSectionEntityType;
  entityGuid: string;
  title: string;
  bodyJson: string | null;
  bodyPlain: string | null;
  sortOrder: number;
  isVisible: boolean;
  allowsContributions: boolean;
  sourceTemplateGuid: string | null;
  createdBy: string;
  translations: string | null;
  createdAt: string;
  updatedAt: string;
  /**
   * DEV-903 Phase K (migration 224). Typed knowledge identity:
   * overview | methodologies | best_practice | risks | lesson_learned |
   * solution_used | problem_addressed | custom.
   */
  sectionType: string;
  /**
   * DEV-903 Phase K. Set on an instance-local override that shadows a
   * playbook master's baseline section (copy-on-write back-link).
   */
  overridesSectionGuid: string | null;
  /**
   * DEV-903 Phase K. Computed by the API: TRUE when this section is the
   * playbook master's row served to an instance as its LIVE baseline
   * (no local override). PUT on its guid copy-on-writes an override;
   * DELETE of an override reverts to the baseline; baselines cannot be
   * deleted from an instance.
   */
  isBaseline: boolean;
}

export interface CreateContentSectionRequest {
  title: string;
  bodyJson?: string;
  bodyPlain?: string;
  sortOrder?: number;
  isVisible?: boolean;
  /** DEV-903 Phase K. Optional typed knowledge identity; defaults to 'custom'. */
  sectionType?: string;
}

export interface UpdateContentSectionRequest {
  title?: string;
  bodyJson?: string | null;
  bodyPlain?: string | null;
  sortOrder?: number;
  isVisible?: boolean;
}

export interface ReorderItem {
  sectionGuid: string;
  sortOrder: number;
}

/** Build the route base for the given entity context. */
function routeBase(entityType: ContentSectionEntityType, entityGuid: string): string {
  const g = normalizeGuid(entityGuid);
  if (entityType === "mini_project") return `/api/mini-projects/${g}/sections`;
  if (entityType === "chronicle") return `/api/chronicles/${g}/sections`;
  return `/api/Projects/${g}/sections`;
}

// ── Fetch functions (entity-polymorphic) ─────────────────────────

export async function fetchSections(
  entityType: ContentSectionEntityType,
  entityGuid: string,
): Promise<ProjectContentSectionResponse[]> {
  return apiFetch<ProjectContentSectionResponse[]>(routeBase(entityType, entityGuid));
}

export async function createSection(
  entityType: ContentSectionEntityType,
  entityGuid: string,
  body: CreateContentSectionRequest,
): Promise<ProjectContentSectionResponse> {
  return apiFetch<ProjectContentSectionResponse>(
    routeBase(entityType, entityGuid),
    { method: "POST", body: JSON.stringify(body) },
  );
}

export async function updateSection(
  entityType: ContentSectionEntityType,
  entityGuid: string,
  sectionGuid: string,
  body: UpdateContentSectionRequest,
): Promise<void> {
  const sg = normalizeGuid(sectionGuid);
  await apiFetch<void>(
    `${routeBase(entityType, entityGuid)}/${sg}`,
    { method: "PUT", body: JSON.stringify(body) },
  );
}

export async function deleteSection(
  entityType: ContentSectionEntityType,
  entityGuid: string,
  sectionGuid: string,
): Promise<void> {
  const sg = normalizeGuid(sectionGuid);
  await apiFetch<void>(
    `${routeBase(entityType, entityGuid)}/${sg}`,
    { method: "DELETE" },
  );
}

export async function reorderSections(
  entityType: ContentSectionEntityType,
  entityGuid: string,
  sections: ReorderItem[],
): Promise<void> {
  await apiFetch<void>(
    `${routeBase(entityType, entityGuid)}/reorder`,
    { method: "PUT", body: JSON.stringify({ sections }) },
  );
}

// ── Legacy aliases (deprecated) ──────────────────────────────────
// Keep the project-only function names as thin wrappers so any consumer
// that hasn't migrated to the polymorphic API still compiles. Remove
// after a sweep confirms no remaining consumers.

/** @deprecated Use fetchSections('project', projectGuid) */
export const fetchProjectSections = (projectGuid: string) =>
  fetchSections("project", projectGuid);

/** @deprecated Use createSection('project', projectGuid, body) */
export const createProjectSection = (projectGuid: string, body: CreateContentSectionRequest) =>
  createSection("project", projectGuid, body);

/** @deprecated Use updateSection('project', projectGuid, sectionGuid, body) */
export const updateProjectSection = (projectGuid: string, sectionGuid: string, body: UpdateContentSectionRequest) =>
  updateSection("project", projectGuid, sectionGuid, body);

/** @deprecated Use deleteSection('project', projectGuid, sectionGuid) */
export const deleteProjectSection = (projectGuid: string, sectionGuid: string) =>
  deleteSection("project", projectGuid, sectionGuid);

/** @deprecated Use reorderSections('project', projectGuid, items) */
export const reorderProjectSections = (projectGuid: string, items: ReorderItem[]) =>
  reorderSections("project", projectGuid, items);
